<template>
	<div
		v-if="appIsInitialized"
		id="tagging-tool"
		class="tagging-tool"
		:class="{
			mobile: isMobileViewport,
			landscape: isLandscape,
			portrait: isMobilePortrait,
			'touch-device': isTouchDevice,
		}"
	>
		<VideoCreateForm
			v-if="isCreatingVideo"
			:event-tags="sourceEventTags"
			modal-type="modal-md"
		/>

		<!-- loading spinner -->
		<LoadingSpinner
			v-if="showLoader"
			class="tagging-tool__spinner"
			center
		/>
		<LayoutTaggingTool>
			<template
				v-if="!isNarrowTaggingTool"
				#actions-left
			>
				<TagCenter />
			</template>
			<template #media>
				<ErrorScreen
					v-if="hasErrorScreen"
					:screen="errorScreen"
				/>
				<template v-else>
					<div
						v-adaptive-height="{ target: '.js-player' }"
						class="tagging-tool__media"
					>
						<div class="l-asport-one-panel">
							<PlayerWrapper
								key="player"
								class="tagging-tool__player"
								:autoplay="false"
								@change-duration="handleDurationUpdate"
							/>
						</div>
						<div
							v-if="isMobileViewport"
							class="tagging-tool__in-player-toast-wrapper"
						>
							<InPlayerToast />
						</div>
						<div
							v-if="!isMobileViewport"
							class="l-asport-one-panel -enable-overflow tagging-tool__timeline-w"
						>
							<TimelineBlock
								class="tagging-tool__timeline"
								:is-tagging-tool="true"
								:duration="duration"
								:elapsed="position"
							/>
						</div>
					</div>
				</template>
			</template>
			<template #actions>
				<ActionBlock
					class="l-asport-one-panel -no-padding"
					:elapsed="position"
				/>
			</template>
		</LayoutTaggingTool>
		<ServerErrorBlock />
		<!-- share -->
		<ShareModal
			v-if="showShare"
			class="tagging-tool__custom-modals"
			:title="shareTitle"
			:event-title="eventTitle"
			:item="shareItem"
			:item-label="itemLabel"
			:links="shareLinks"
			:url="shareUrl"
			:is-event="true"
			:url-label="urlLabel"
			:image-not-found-label="imageNotFoundLabel"
			@close="handleShareClose"
		/>
		<!-- context menu mobile -->
		<ContextMenuMobile
			v-if="isMobileViewport"
			class="tagging-tool__custom-modals"
		/>

		<!-- Time picker modal -->
		<TimePickerModalMobile v-if="isMobileViewport" />

		<ConfirmModal
			v-if="confirmReload"
			primary-btn-class="button--primary"
			:title="trans('one.stream_restarted')"
			:confirm-label="transUcfirst('commons.reload')"
			:cancel-label="transUcfirst('commons.cancel')"
			@confirm="handleConfirmReload"
		>
			<template #body>
				<p>
					{{ trans('one.stream_restarted_info') }}
				</p>
			</template>
		</ConfirmModal>

		<div
			v-show="showTagCreateToast"
			class="tagging-tool__tag-create-toast"
			@mouseenter="handleTagCreateToastHover"
			@mouseleave="handleTagCreateToastLeave"
		>
			<div class="tagging-tool__tag-create-toast-left">
				<Button
					class="tagging-tool__tag-create-toast-button"
					icon="e-remove"
					modifier="borderless"
					theme="dark"
					size="small"
					@click.prevent="closeCreateToast"
				/>
				<h5 class="tagging-tool__tag-create-toast-title">
					{{ $I18n.trans('commons.type_created', { type: $I18n.trans_choice('commons.tag', 1) }) }}
				</h5>
			</div>
			<div class="tagging-tool__tag-create-toast-actions">
				<span
					class="tagging-tool__tag-create-toast-action -edit"
					@click.prevent="handleEditTag"
				>{{ $Helpers.ucfirst($I18n.trans('commons.edit')) }}</span>
				<span
					class="tagging-tool__tag-create-toast-action -undo"
					@click.prevent="handleUndoTag"
				>{{ $Helpers.ucfirst($I18n.trans('commons.undo')) }}</span>
			</div>
		</div>
	</div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { bus, EVENT } from '@videotools/js/events';
import AdaptiveHeight from '@shared/directives/v-adaptive-height';

import LayoutTaggingTool from '../components/LayoutTaggingTool';
import PlayerWrapper from '@components/player/PlayerWrapper';
import TimelineBlock from '@videotools/js/blocks/Timeline';
import ActionBlock from '../blocks/Action';
import ServerErrorBlock from '../../blocks/ServerError';
import ShareModal from '@components/share-modal';
import Button from '@shared/components/Button';
import LoadingSpinner from '@components/loader/LoadingSpinner';
import { ContextMenuMobile } from '@components/context-menu';
import InPlayerToast from '@components/notifications/InPlayerToast';
import TimePickerModalMobile from '@components/modal/TimePickerModalMobile';
import ConfirmModal from '@components/modal/ConfirmModal';
import ErrorScreen from '@videotools/js/components/ErrorScreen';
import TagCenter from '@videotools/js/one/components/TagCenter/TagCenter';
import VideoCreateForm from '@js/one/components/Video/VideoCreateForm';

const SHARE_TYPE = {
	EVENT: 'event',
	TAG:   'tag',
};

const busEvents = [
	EVENT.SHARE_TAG,
	EVENT.SHARE_EVENT,
	EVENT.PLAYER.ON.PROGRESS,
	EVENT.STREAM_START_CHANGED,
	EVENT.TAG_CREATE,
];

export default {
	name:       'TaggingTool',
	components: {
		VideoCreateForm,
		TimePickerModalMobile,
		InPlayerToast,
		LayoutTaggingTool,
		ActionBlock,
		TimelineBlock,
		ServerErrorBlock,
		ShareModal,
		ContextMenuMobile,
		LoadingSpinner,
		PlayerWrapper,
		ConfirmModal,
		ErrorScreen,
		TagCenter,
		Button,
	},
	directives: {
		'adaptive-height': AdaptiveHeight,
	},
	data() {
		return {
			// media
			duration:                0,
			position:                0,
			// share
			showShare:               false,
			shareItem:               null,
			shareType:               null,
			confirmReload:           false,
			showTagCreateToast:      false,
			toastTimeout:            null,
			currentTag:              null,
			isTagCreateToastHovered: false,
		};
	},
	computed: {
		...mapGetters({
			appIsInitialized:       'app/isInitialized',
			event:                  'event/event',
			eventTags:              'event/tags',
			eventTitle:             'event/eventTitle',
			canUpdateEvent:         'event/canUpdateEvent',
			canUpdateTagTemplates:  'permissions/canUpdateTagTemplates',
			canCreateHighlightRule: 'permissions/canCreateHighlightRule',
			isSaving:               'eventTagEdit/isSaving',
			sportName:              'event/sportName',
			isMobileViewport:       'device/isMobileViewport',
			isNarrowTaggingTool:    'device/isNarrowTaggingTool',
			isLandscape:            'device/isLandscape',
			isMobilePortrait:       'device/isMobilePortrait',
			isTouchDevice:          'device/isTouchDevice',
			isFullscreen:           'device/isFullscreen',
			supportsNativeShare:    'device/supportsNativeShare',
			participantById:        'event/participantById',
			hasErrorScreen:         'error/hasErrorScreen',
			errorScreen:            'error/errorScreen',
			matchTags:              'tags/matchTags',
			isCreatingVideo:        'videoCreate/isActive',
			sourceEventTags:        'videoCreate/sourceEventTags',
		}),
		showLoader() {
			return this.isSaving;
		},
		shareTitle() {
			switch (this.shareType) {
			case SHARE_TYPE.EVENT:
				return this.shareLabels.SHARE_EVENT;
			case SHARE_TYPE.TAG:
				return this.shareLabels.SHARE_TAG;
			default:
				return this.shareLabels.SHARE;
			}
		},
		itemLabel() {
			const parentCategory = this.$Helpers.ucfirst(this.$I18n.trans('event.category.parent')[this.event.categoryParent] ?? '');
			const childCategory = this.$Helpers.ucfirst(this.$I18n.trans('event.category.child')[this.event.categoryChild] ?? '');
			return `${parentCategory} • ${childCategory}`;
		},
		shareUrl() {
			if (!this.shareItem) {
				return '';
			}
			return this.shareItem.url;
		},
		shareLinks() {
			return this.shareItem?.shareLinks ?? { facebook: '', twitter: '', mail: '' };
		},
		urlLabel() {
			return this.shareLabels.COPY_LINK;
		},
		imageNotFoundLabel() {
			return this.shareLabels.PREVIEW_NOT_FOUND;
		},
	},
	watch: {
		isFullscreen: {
			handler:   'lockOrientation',
			immediate: true,
		},
	},
	async created() {
		this.setAppTypeTaggingTool();
		this.setAppStateInitializing();
		if (this.canCreateHighlightRule && this.canUpdateEvent) {
			await this.loadHighlightRules();
		}
		this.setAppStateNormal();

		for (const event of busEvents) {
			bus.subscribe(event, this[`BUS-${event}`]);
		}
	},
	destroyed() {
		for (const event of busEvents) {
			bus.unsubscribe(event, this[`BUS-${event}`]);
		}
	},
	methods: {
		...mapActions({
			setAppStateInitializing: 'app/setAppStateInitializing',
			setAppStateNormal:       'app/setAppStateNormal',
			setAppTypeTaggingTool:   'app/setAppTypeTaggingTool',
			lockOrientation:         'device/lockOrientation',
			loadHighlightRules:      'event/loadHighlightRules',
			updateEventTags:         'event/updateEventTags',
			timeShiftEventTags:      'event/timeShiftEventTags',
			deleteEventTag:          'event/deleteEventTag',
			editEventTag:            'eventTagEdit/editEventTag',
			shareNative:             'device/shareNative',
		}),
		[`BUS-${EVENT.SHARE_TAG}`](tag) {
			this.shareItem = tag;
			this.shareType = SHARE_TYPE.TAG;

			if (this.isTouchDevice && this.supportsNativeShare) {
				this.shareNative({
					url: this.shareUrl,
				});
			} else {
				this.showShare = true;
			}
		},
		[`BUS-${EVENT.SHARE_EVENT}`]() {
			this.shareItem = this.event;
			this.shareType = SHARE_TYPE.EVENT;
			this.showShare = true;
			if (this.isTouchDevice && this.supportsNativeShare) {
				this.shareNative({
					url:  this.shareUrl,
					text: this.eventTitle,
				});
			} else {
				this.showShare = true;
			}
		},
		[`BUS-${EVENT.PLAYER.ON.PROGRESS}`](value) {
			this.position = value;
		},
		[`BUS-${EVENT.STREAM_START_CHANGED}`]() {
			this.confirmReload = true;
		},
		[`BUS-${EVENT.TAG_CREATE}`](tag) {
			this.currentTag = tag;
			this.showTagCreateToast = true;
			this.startToastTimer();
		},
		handleDurationUpdate(val) {
			this.duration = val;
		},
		handleShareClose() {
			this.showShare = false;
		},
		handleConfirmReload() {
			window.location.reload();
		},
		handleTagCreateToastHover() {
			this.isTagCreateToastHovered = true;
			if (this.toastTimeout) {
				clearTimeout(this.toastTimeout);
			}
		},
		handleTagCreateToastLeave() {
			this.isTagCreateToastHovered = false;
			this.startToastTimer();
		},
		startToastTimer() {
			if (this.toastTimeout) {
				clearTimeout(this.toastTimeout);
			}
			if (!this.isTagCreateToastHovered) {
				this.toastTimeout = setTimeout(() => {
					this.showTagCreateToast = false;
				}, 5000);
			}
		},
		closeCreateToast() {
			this.showTagCreateToast = false;
		},
		handleEditTag() {
			if (this.currentTag) {
				this.editEventTag(this.currentTag);
				this.showTagCreateToast = false;
			}
		},
		handleUndoTag() {
			if (this.currentTag) {
				this.deleteEventTag(this.currentTag);
				this.showTagCreateToast = false;
			}
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@shared/sass/shared-variables';
@import '@videotools/js/components/timeline/timeline.scss';

.l-tagging-tool-section {
	padding: $sp4 var(--one-panel-h-padding);
	background-color: var(--one-panel-background-color);
	border-radius: $border-radius--sm;
}

.tagging-tool {
	height: 100%;
}

.tagging-tool__media {
	display: flex;
	flex-direction: column;
	gap: var(--one-panel-gap);
	width: 100%;
	height: 100%;
}

.tagging-tool__player {
	@include z-index('player', 'tagging-tool');
	position: relative;
	margin-bottom: $sp2;
}

.tagging-tool__timeline-w {
	@include z-index('tag-timeline', 'tagging-tool');
	position: relative;
}

// .tagging-tool__timeline {}

.tagging-tool__custom-modals {
	position: absolute;
	width: 100%;
	bottom: 0;
	left: 0;
	height: auto;
}

.tagging-tool__spinner {
	@include z-index('spinner', 'tagging-tool');
	position: absolute;
	background: rgba(0, 0, 0, 0.4);
}

.tagging-tool__in-player-toast-wrapper {
	@include z-index('in-player-toast', 'tagging-tool');
	position: absolute;
	bottom: 6px;
	width: 100%;
	overflow: hidden;
}

.tagging-tool__tag-create-toast {
	@include z-index('in-player-toast', 'tagging-tool');
	position: absolute;
	bottom: $sp5;
	right: $sp5;
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: $sp6;
	background: var(--color-grey-800);
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
	border-radius: 2px;
	padding: $sp3 $sp4 $sp3 $sp3;
	font-size: 14px;
}

.tagging-tool__tag-create-toast-left {
	display: flex;
	align-items: center;
	gap: $sp2;
}

.tagging-tool__tag-create-toast-title {
	font-size: 14px;
	font-weight: 400;
}

.tagging-tool__tag-create-toast-actions {
	margin-left: 12px;
	display: flex;
	gap: $sp4;
}

.tagging-tool__tag-create-toast-action {
	color: var(--color-primary);
	text-decoration: none;
	cursor: pointer;

	&.-undo {
		color: var(--color-hype-yellow);
	}

	&:hover {
		text-decoration: underline;
	}
}
</style>
